/**
 * Error codes shared between backend and frontend.
 * These codes are used for translating error messages via i18n.
 */
export const ERROR_CODES = {
  // General
  UNKNOWN: "UNKNOWN",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",

  // Authorization
  UNAUTHORIZED: "UNAUTHORIZED",
  NOT_FOUND: "NOT_FOUND",
  FORBIDDEN: "FORBIDDEN",
  SESSION_COMPLETED: "SESSION_COMPLETED",
  SESSION_LOCKED: "SESSION_LOCKED",

  // Validation
  VALIDATION_ERROR: "VALIDATION_ERROR",
  SHARES_MISMATCH: "SHARES_MISMATCH",
  DUPLICATE: "DUPLICATE",
  ALREADY_PAID: "ALREADY_PAID",
  ALREADY_CONFIRMED: "ALREADY_CONFIRMED",
  NOT_PAID: "NOT_PAID",
  HAS_EXPENSES: "HAS_EXPENSES",
  ALREADY_COMPLETED: "ALREADY_COMPLETED",
  ALREADY_ACTIVE: "ALREADY_ACTIVE",
  AI_NOT_CONFIGURED: "AI_NOT_CONFIGURED",
  AI_ERROR: "AI_ERROR",
  UPDATE_FAILED: "UPDATE_FAILED",
  INVITATION_EXPIRED: "INVITATION_EXPIRED",
  INVITATION_ALREADY_USED: "INVITATION_ALREADY_USED",
  ALREADY_GROUP_MEMBER: "ALREADY_GROUP_MEMBER",
  POLL_CLOSED: "POLL_CLOSED",
  ALREADY_VOTED: "ALREADY_VOTED",
  SCHEDULE_CLOSED: "SCHEDULE_CLOSED",

  // Auth (Better Auth related)
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  EMAIL_ALREADY_EXISTS: "EMAIL_ALREADY_EXISTS",
  INVALID_EMAIL: "INVALID_EMAIL",
  AUTH_ERROR: "AUTH_ERROR",

  // Better Auth specific codes (from @better-auth/core)
  USER_NOT_FOUND: "USER_NOT_FOUND",
  INVALID_PASSWORD: "INVALID_PASSWORD",
  INVALID_EMAIL_OR_PASSWORD: "INVALID_EMAIL_OR_PASSWORD",
  USER_ALREADY_EXISTS: "USER_ALREADY_EXISTS",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  PASSWORD_TOO_SHORT: "PASSWORD_TOO_SHORT",
  PASSWORD_TOO_LONG: "PASSWORD_TOO_LONG",
  EMAIL_NOT_VERIFIED: "EMAIL_NOT_VERIFIED",
  ACCOUNT_NOT_FOUND: "ACCOUNT_NOT_FOUND",
  FAILED_TO_CREATE_USER: "FAILED_TO_CREATE_USER",
  FAILED_TO_CREATE_SESSION: "FAILED_TO_CREATE_SESSION",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/**
 * HTTP status code to error code mapping.
 * Used when the backend doesn't provide a specific error code.
 */
export const HTTP_STATUS_TO_ERROR_CODE: Record<number, ErrorCode> = {
  400: ERROR_CODES.VALIDATION_ERROR,
  401: ERROR_CODES.UNAUTHORIZED,
  403: ERROR_CODES.FORBIDDEN,
  404: ERROR_CODES.NOT_FOUND,
  500: ERROR_CODES.INTERNAL_ERROR,
  503: ERROR_CODES.INTERNAL_ERROR,
};

/**
 * Get error code from HTTP status code
 */
export function getErrorCodeFromStatus(status: number): ErrorCode {
  return HTTP_STATUS_TO_ERROR_CODE[status] || ERROR_CODES.UNKNOWN;
}
